import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Paper, Typography } from '@mui/material';
import { isNaN } from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { StockReport } from '../../interfaces/Report/StockReport/StockReport';
import {
  getDefaultStockReportFilter,
  StockReportFilter,
  StockReportOrderField,
} from '../../interfaces/Report/StockReport/StockReportFilter';
import { getStockReport } from '../../api/Report';
import { Pagination } from '../../interfaces/Pagination';
import { sharedColors, sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import NumberInput from '../../components/NumberInput/NumberInput';
import { StockReportTable } from './StockReportTable';
import { SortingOrder } from '../../interfaces/SortingOrder';

export const StockReportPage = () => {
  const [report, setReport] = useState<PaginatedResponse<StockReport>>({
    data: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(getDefaultStockReportFilter());
  const [loading, setLoading] = useState(false);

  const refreshReport = (filters?: StockReportFilter) => {
    setLoading(true);
    getStockReport(filters ?? filter)
      .then((r) => setReport(r))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshReport();
  }, []);

  const handleSearch = () => {
    const newFilter: StockReportFilter = {
      ...filter,
      pagination: { ...filter.pagination, page: 0 },
    };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  const handleOrder = (orderBy: StockReportOrderField, order: SortingOrder) => {
    const newFilter: StockReportFilter = { ...filter, orderBy, order };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  const handlePagination = (p: Pagination) => {
    const newFilter: StockReportFilter = { ...filter, pagination: p };

    setFilter(newFilter);
    refreshReport(newFilter);
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />

      <Typography variant='h5' sx={{ ...sharedStyles.pageTitle }}>
        Ürün Stok Raporu
      </Typography>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              minWidth: 150,
              maxWidth: 200,
              flexGrow: 1,
              backgroundColor: sharedColors.white,
              height: 'fit-content',
            }}
          >
            <NumberInput
              label='Minimum Stok'
              value={filter.minQuantity}
              onChange={(x) =>
                setFilter({ ...filter, minQuantity: isNaN(x) ? undefined : x })
              }
              onEnter={handleSearch}
            />
          </Box>

          <Box
            sx={{
              minWidth: 150,
              maxWidth: 200,
              flexGrow: 1,
              backgroundColor: sharedColors.white,
              height: 'fit-content',
            }}
          >
            <NumberInput
              label='Maksimum Stok'
              value={filter.maxQuantity}
              onChange={(x) =>
                setFilter({ ...filter, maxQuantity: isNaN(x) ? undefined : x })
              }
              onEnter={handleSearch}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() =>
                setFilter({
                  ...getDefaultStockReportFilter(),
                  pagination: filter.pagination,
                })
              }
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtrele
            </Button>
          </Box>
        </Box>
      </Paper>

      <StockReportTable
        rows={report.data}
        totalCount={report.totalCount}
        orderBy={filter.orderBy}
        order={filter.order}
        onChangeOrder={handleOrder}
        pagination={filter.pagination}
        onPagination={handlePagination}
      />
    </Box>
  );
};
